import type { ReactNode } from "react";

// Lightweight, theme-aware, CSS-only tooltips to help colleagues navigate the
// board. No positioning library — hover/focus reveals a bubble above the anchor;
// `align` shifts it so headers near the page edge don't clip. `Tooltip` wraps any
// anchor; `InfoHint` is the small ⓘ-icon convenience used next to headings.

type Align = "center" | "start" | "end";
const POS: Record<Align, string> = {
  center: "left-1/2 -translate-x-1/2",
  start: "left-0",
  end: "right-0",
};

export function Tooltip({
  text,
  align = "center",
  children,
  className = "",
}: {
  text: string;
  align?: Align;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={`group relative inline-flex ${className}`} tabIndex={0}>
      {children}
      <span
        role="tooltip"
        className={[
          "pointer-events-none absolute bottom-full z-50 mb-1.5 hidden w-56 rounded-lg border border-edge bg-surface px-2.5 py-1.5",
          // reset any inherited header styling so the copy always reads normally
          "text-xs font-normal normal-case leading-snug tracking-normal text-secondary shadow-xl",
          "group-hover:block group-focus:block",
          POS[align],
        ].join(" ")}
      >
        {text}
      </span>
    </span>
  );
}

export function InfoHint({
  text,
  align = "center",
  className = "",
}: {
  text: string;
  align?: Align;
  className?: string;
}) {
  return (
    <Tooltip text={text} align={align} className={`align-middle ${className}`}>
      <svg
        className="h-3.5 w-3.5 cursor-help text-muted transition-colors hover:text-secondary"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
        data-testid="info-hint"
      >
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9a1 1 0 012 0v4a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z"
          clipRule="evenodd"
        />
      </svg>
      <span className="sr-only">{text}</span>
    </Tooltip>
  );
}
